import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';

const stats = [
  { label: '文章数量', value: '15+' },
  { label: '内容分类', value: '5' },
  { label: '累计字数', value: '50,000+' },
  { label: '更新年份', value: '2026' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '内容权威',
    desc: '所有文章均经过多方资料核实与交叉验证，确保信息的准确性与可靠性，为读者提供值得信赖的知识来源。',
  },
  {
    icon: 'ri-eye-line',
    title: '客观中立',
    desc: '我们坚持客观、中立的内容立场，不带有主观偏见，全面呈现NTR文化的各个方面，让读者形成自己的认知。',
  },
  {
    icon: 'ri-book-open-line',
    title: '知识深度',
    desc: '从基本定义到文化分析，从历史溯源到当代讨论，我们提供由浅入深的知识体系，满足不同层次读者的学习需求。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '读者导向',
    desc: '以读者的学习体验为核心，持续优化内容结构与呈现方式，让知识的获取变得简单、高效且愉悦。',
  },
];

export default function About() {
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = formData.get('website_alt')?.toString()?.trim();
    if (honeypotVal) {
      setFormSuccess(true);
      setFormError('');
      return;
    }

    formData.delete('website_alt');

    const params = new URLSearchParams();
    formData.forEach((val, key) => {
      params.append(key, val.toString());
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4gkt3pcjqs2fcsm10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        setFormError('');
        form.reset();
        setTimeout(() => setFormSuccess(false), 5000);
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormSuccess(false);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormSuccess(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于 NTR 深度解析
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              我们致力于为中文用户打造最全面、最深入的NTR文化知识平台，让每一位对NTR感兴趣的读者都能在这里找到权威、客观、系统的知识内容。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-4">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              品牌故事
            </h2>
            <div className="text-foreground-600 leading-relaxed space-y-4 text-[0.9375rem]">
              <p>
                NTR深度解析（NTR Guide）诞生于2025年，由一群热爱ACG文化、深耕二次元研究的内容创作者共同发起。我们的初心很简单：在中文互联网上，关于NTR的讨论虽然广泛，但系统性、权威性的知识内容却极为稀缺。大量的碎片化信息让初学者难以入门，也让深度爱好者缺乏进一步探索的路径。
              </p>
              <p>
                我们相信，<strong>NTR作为一种独特的文化现象，值得被认真对待和深入研究</strong>。它不仅仅是ACG作品中的情节标签，更是折射了人类情感复杂性、社会关系变迁和创作美学的多维棱镜。因此，我们决定创建一个专注于此的百科式知识平台。
              </p>
              <p>
                经过数月的筹备与内容打磨，NTR深度解析现已成为中文圈内最全面的NTR知识库之一，覆盖定义解析、历史溯源、文化分析、作品推荐等多个维度。我们持续更新内容，紧跟NTR文化的最新发展动态。
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              我们的理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                    <i className={`${v.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2">
                    {v.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
              声誉与认可
            </h2>
            <div className="flex flex-col gap-4">
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-2 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "作为ACG文化研究者，我一直在寻找一个对NTR进行全面且客观分析的平台。NTR深度解析不仅在内容深度上令人满意，更在学术性视角上超出了我的预期。强烈推荐给每一个想深入了解NTR文化的人。"
                </p>
                <p className="text-xs text-foreground-500">—— 二次元文化研究社群</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-2 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "之前对NTR只有很浅薄的了解，通过这个网站系统性地学习了NTR的起源、发展和文化背景。文章质量非常高，逻辑清晰，引证充分。不愧是中文圈最专业的NTR百科。"
                </p>
                <p className="text-xs text-foreground-500">—— 知乎匿名用户</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-2 text-accent-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  "网站设计简洁大方，内容组织合理，尤其是分类导航做得很好。作为刚入门的NTR爱好者，这里是我每天必看的知识来源。感谢创作团队的用心付出！"
                </p>
                <p className="text-xs text-foreground-500">—— 哔哩哔哩用户</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm text-center mb-8">
              有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
            </p>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <form onSubmit={handleSubmit} data-readdy-form="true">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                {formError && (
                  <p className="text-sm text-red-600 mb-3">{formError}</p>
                )}
                {formSuccess && (
                  <p className="text-sm text-accent-600 mb-3">感谢您的留言，我们会尽快回复！</p>
                )}
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  提交留言
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800 mb-1">邮箱</p>
                <p className="text-xs text-foreground-500">contact@yangtuodaxia.cn</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-50 text-accent-600">
                  <i className="ri-global-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800 mb-1">网站</p>
                <p className="text-xs text-foreground-500">https://yangtuodaxia.cn</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800 mb-1">响应时间</p>
                <p className="text-xs text-foreground-500">1-3 个工作日内</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}